import { nanoid } from 'nanoid';

import { type ADFDocument } from '@canvas/adf';
import { AbortCommentExperienceCommand } from '@canvas/api-comment/commands';
import {
  type ConfluenceService,
  getConfluenceService,
} from '@canvas/atlassian-service/confluence';
import { type ConfluenceComment } from '@canvas/atlassian-service/confluence/types';
import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { commandService } from '@canvas/command/singleton';
import { globalErrorManager } from '@canvas/error-reporting';
import { CanvasNonFatalError } from '@canvas/error-reporting/types';
import { type FrameContext } from '@canvas/frame-api-types';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { fetchReactions } from '../comment-reactions/fetch-from-store.ts';
import {
  type CommentStoreSubscriber,
  type CommentStore,
  type CommentThreadEvent,
  type CommentThread,
  type DraftId,
} from '../comment-store/comment-store-interface.ts';
import { CommentContentType } from '../comment-types.ts';

import { commentFromConfluenceComment } from './comment-loader.ts';
import {
  optimisticAdd,
  optimisticDelete,
  optimisticUpdate,
  type PersistentWriter,
  type OptimisticWriter,
  persistentAdd,
  persistentUpdate,
  optimisticResolve,
  optimisticReOpen,
} from './comment-store-writers.ts';

/**  Since comments are using optimistic rendering, the backend permissions could change in the background and throw these errors
 * This list was compiled with the following splunk query:
 *
 * `micros_ufo` "canvas.fe.operation.comment-interaction.reliability" FAILED "experience measured"
 * ext.event.attributes.properties.experience:key="canvas.fe.operation.comment-interaction.reliability"
 * ext.event.attributes.properties.experience:status=FAILED env="prod*"
 * | rex field=ext.event.attributes.properties.custom:errorMessage mode=sed "s/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/<uuid>/g"
 * | stats count by ext.event.attributes.properties.custom:errorMessage, ext.event.attributes.properties.custom:commentAction
 */
const abortableMessagePatterns = [
  /Write operations are not allowed in read-only mode/,
  /Error resolving the comment NOT_PERMITTED/,
  /PermissionException/,
];

export const isAbortableExperienceErrorMessage = (message: string) => {
  for (const pattern of abortableMessagePatterns) {
    if (pattern.test(message)) {
      return true;
    }
  }
  return false;
};

function isNotFoundError(error: unknown) {
  if (error instanceof Error && error.message.includes('No comments found')) {
    return true;
  }

  if (
    typeof error === 'object' &&
    error != null &&
    'extensions' in error &&
    typeof error.extensions === 'object' &&
    error.extensions != null &&
    'statusCode' in error.extensions &&
    error.extensions.statusCode === 404
  ) {
    return true;
  }

  return false;
}

// We want to abort the comment interaction so we don't burn our SLO due to user lacking permissions to interact with comments on the backend
// This is a temporary solution until we have a better way to handle this
export const checkAbortableExperienceError = (
  error: unknown,
  captureLocation: string,
) => {
  const msg = error instanceof Error ? error.message : String(error);

  if (isAbortableExperienceErrorMessage(msg)) {
    commandService.send(AbortCommentExperienceCommand);
  } else {
    globalErrorManager.captureNonFatalError(CanvasNonFatalError.COMMENT_STORE, error, {
      captureLocation,
    });
  }
};
/**
 * The primary store for Confluence-owned comments. Makes use of optimistic
 * updates for snappy rendering and UX feedback around comments.
 *
 * @description {@link CommentThreadStore.writeOptimisticUpdate} is used to
 * update all local state immediately upon a user interaction.
 * @description {@link CommentThreadStore.writePersistentUpdate} is used to
 * update all local state upon local state being successfully pushed to remote.
 */
export class CommentThreadStore implements CommentStore {
  /**
   * All root-level comments and their replies (only one level deep).
   * @description Here we store all the root-level comments and their replies
   * which is one level deep i.e. the replies don't have more replies etc.
   */
  private threads: Map<string, CommentThread> = new Map();

  /**
   * All subscribers to events which the {@link CommentThreadStore} emits.
   * @description Used to act on changes in state.
   */
  private subscribers: CommentStoreSubscriber[] = [];

  private mapOfExternalIdToThreadId: Map<string, string> = new Map();
  private mapOfThreadIdToExternalId: Map<string, string> = new Map();
  private currentOpenedThread: string | undefined = undefined;

  private commentDrafts: Map<DraftId, ADFDocument> = new Map();

  public constructor(
    private getBoardId: () => string,
    private service: ConfluenceService,
  ) {}

  public subscribe(subscription: CommentStoreSubscriber) {
    this.subscribers.push(subscription);
  }

  public unsubscribe(subscription: CommentStoreSubscriber) {
    this.subscribers = this.subscribers.filter((s) => s !== subscription);
  }

  public emit(event: CommentThreadEvent) {
    this.subscribers.forEach((subscriber) => subscriber(event));
  }

  public getCurrentOpenedThread() {
    return this.currentOpenedThread;
  }

  public updateCurrentOpenedThread(threadId: string | undefined) {
    this.currentOpenedThread = threadId;
    this.emit({ type: 'threadSelected' });
  }

  public mapExternalIdToThreadId(externalId: string, threadId: string) {
    this.mapOfExternalIdToThreadId.set(externalId, threadId);
    this.mapOfThreadIdToExternalId.set(threadId, externalId);
    this.emit({ type: 'threadAssociated' });
  }

  public getThreadIdFromExternalId(externalId: string) {
    return this.mapOfExternalIdToThreadId.get(externalId);
  }

  public getExternalIdFromThreadId(threadId: string) {
    return this.mapOfThreadIdToExternalId.get(threadId);
  }

  public async addThread(
    content: ADFDocument,
    threadId: string,
    createdFrom: CommentContentType = CommentContentType.WHITEBOARD,
    originalSelection?: string,
  ) {
    const adf = JSON.stringify(content);
    const boardId = this.getBoardId();

    const optimisticId = nanoid();

    this.writeOptimisticUpdate(
      threadId,
      undefined,
      {
        content: adf,
        optimisticId,
        threadId: threadId,
        type: 'root',
      },
      optimisticAdd,
    );

    try {
      const created = await this.service.addComment({
        boardId,
        content: adf,
        parentCommentId: undefined,
        createdFrom,
        originalSelection,
      });

      // This handles the case where there is a backend comment ID being
      // created. We want to now map it to the thread ID.
      this.mapExternalIdToThreadId(created.id, threadId);

      this.writePersistentUpdate(threadId, optimisticId, created, persistentAdd);
      this.emit({
        type: 'threadCreated',
        threadId,
        externalId: created.id,
      });
      this.emit({
        type: 'rootCommentChanged',
      });

      return { threadId, externalId: created.id, contentId: created.reactionsId };
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'addThread-catch-all');
      this.threads.delete(threadId);
      throw e;
    }
  }

  public getExternalId(commentId: string | undefined) {
    return commentId ? this.toContentId(commentId) : undefined;
  }

  public getRootCommentFromThreadId(
    threadId: string | undefined,
    threads: Map<string, CommentThread>,
  ) {
    if (!threadId) {
      return;
    }
    const thread = threads.get(threadId);
    if (!thread || thread.status === 'loading') {
      return;
    }
    return thread.comments[0] ?? undefined;
  }

  public async addReply(
    content: ADFDocument,
    threadId: string,
    createdFrom: CommentContentType = CommentContentType.WHITEBOARD,
  ) {
    const thread = this.threads.get(threadId);
    if (!thread || thread?.status === 'loading') {
      return;
    }

    // To clone the object
    const previousThread = JSON.parse(JSON.stringify(thread));
    try {
      const adf = JSON.stringify(content);
      const boardId = this.getBoardId();

      const optimisticIdReply = nanoid();

      // Optimistically update reply
      this.writeOptimisticUpdate(
        threadId,
        undefined,
        {
          content: adf,
          optimisticId: optimisticIdReply,
          threadId,
          type: 'reply',
        },
        optimisticAdd,
      );

      const rootComment = thread.comments[0];
      const created = await this.service.addComment({
        boardId,
        content: adf,
        parentCommentId: rootComment.id,
        createdFrom,
      });

      this.writePersistentUpdate(threadId, optimisticIdReply, created, persistentAdd);

      return { threadId, externalId: created.id, parentContentId: created.parentId };
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'addReply-catch-all');
      this.revertThread(threadId, previousThread);
      throw e;
    }
  }

  public async updateComment(threadId: string, externalId: string, content: ADFDocument) {
    const thread = this.threads.get(threadId);
    if (!thread || thread?.status === 'loading') {
      return;
    }
    const previousThread = JSON.parse(JSON.stringify(thread));
    try {
      const adf = JSON.stringify(content);

      const rootComment = thread.comments[0];

      this.writeOptimisticUpdate(
        threadId,
        externalId,
        { content: adf },
        optimisticUpdate,
      );

      const updated = await this.service.updateComment({
        content: adf,
        commentId: externalId,
        parentCommentId: rootComment.id,
      });

      this.writePersistentUpdate(threadId, externalId, updated, persistentUpdate);
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'deleteComment-catch-all');
      this.revertThread(threadId, previousThread);
      throw e;
    }
  }

  public async deleteComment(threadId: string, externalId: string) {
    let previousThread: CommentThread | undefined;
    try {
      const thread = await this.getOrInitializeThread(threadId, externalId);
      previousThread = JSON.parse(JSON.stringify(thread));
      if (!previousThread) {
        return;
      }
      this.writeOptimisticUpdate(threadId, externalId, {}, optimisticDelete);

      await this.service.deleteComment({ commentId: externalId });

      this.emit({
        type: 'threadChangedRemotely',
        threadId,
        version: Date.now(),
      });
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'deleteComment-catch-all');
      if (previousThread) {
        this.revertThread(threadId, previousThread);
      }
      throw e;
    }
  }

  public async resolveComment(
    threadId: string,
    externalId: string,
    onResolve?: VoidFunction,
    onReOpen?: VoidFunction,
  ) {
    let previousThread: CommentThread | undefined;
    try {
      const thread = await this.getOrInitializeThread(threadId, externalId);
      previousThread = JSON.parse(JSON.stringify(thread));
      if (!previousThread) {
        return;
      }
      this.writeOptimisticUpdate(threadId, externalId, {}, optimisticResolve);
      onResolve?.();
      await this.service.resolveComment({
        commentId: externalId,
        resolved: true,
      });
      await this.reloadThread(threadId, externalId);
      const reloadedThread = this.threads.get(threadId);
      const resolveSuccessful =
        reloadedThread &&
        reloadedThread.status === 'fetched' &&
        reloadedThread.comments[0].resolved;

      if (!resolveSuccessful) {
        this.emit({ type: 'errored' });
        globalErrorManager.captureNonFatalError(
          CanvasNonFatalError.COMMENT_STORE,
          new Error('Failed to resolve comment'),
          {
            captureLocation: 'resolveComment-resolve-unsuccessful',
          },
        );
        this.revertThread(threadId, previousThread);
        onReOpen?.();
        throw new Error('Failed to resolve comment');
      }

      this.emit({
        type: 'threadResolved',
        onReOpenCallback: () =>
          this.reOpenComment(threadId, externalId, onReOpen, onResolve),
      });
      this.emit({
        type: 'threadChangedRemotely',
        threadId,
        version: Date.now(),
      });
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'resolveComment-catch-all');
      if (previousThread) {
        this.revertThread(threadId, previousThread);
      }
      onReOpen?.();
      throw e;
    }
  }

  public async reOpenComment(
    threadId: string,
    externalId: string,
    onReOpen?: VoidFunction,
    onResolve?: VoidFunction,
  ) {
    const thread = this.threads.get(threadId);
    if (!thread) {
      return;
    }
    const previousThread = JSON.parse(JSON.stringify(thread));
    try {
      this.writeOptimisticUpdate(threadId, externalId, {}, optimisticReOpen);
      onReOpen?.();
      await this.service.resolveComment({
        commentId: externalId,
        resolved: false,
      });
      await this.reloadThread(threadId, externalId);

      const thread = this.threads.get(threadId);
      const reOpenSuccessful =
        thread && thread.status === 'fetched' && !thread.comments[0].resolved;

      if (!reOpenSuccessful) {
        this.emit({ type: 'errored' });
        onResolve?.();
        return;
      }

      this.emit({ type: 'threadChangedRemotely', threadId, version: Date.now() });
    } catch (e: unknown) {
      checkAbortableExperienceError(e, 'reOpenComment-catch-all');
      this.revertThread(threadId, previousThread);
      onResolve?.();
      throw e;
    }
  }

  public toContentId(referenceId: string) {
    return this.service.toContentId(referenceId);
  }

  /**
   * Fetches root level comments by most recent.
   * @param before An optional parameter, when we want to fetch comments *before*
   * a certain comment (used for pagination)
   * @returns A page of comments.
   */
  public async getRootLevelComments(before?: string): Promise<{
    rootLevelThreads: Map<string, CommentThread>;
    totalCount: number;
    cursor?: string;
  }> {
    const boardId = this.getBoardId();
    const rootLevelThreads = new Map();
    const { comments, totalCount, cursor } = await this.service.listComments({
      boardId,
      noCache: true,
      before,
    });

    comments.forEach((externalComment) => {
      const threadId = this.mapOfExternalIdToThreadId.get(externalComment.id);
      if (threadId) {
        const comment = commentFromConfluenceComment(externalComment, threadId, 'root');
        const replies = comment.replies;
        const comments = [comment, ...replies];

        rootLevelThreads.set(threadId, {
          status: 'fetched',
          comments,
        });
      }
    });
    return { rootLevelThreads, totalCount, cursor };
  }

  public async getCommentCount(): Promise<number> {
    const boardId = this.getBoardId();
    const totalCount = await this.service.getCommentCount({
      boardId,
      noCache: true,
    });

    return totalCount;
  }

  public async initializeThread(
    threadId: string,
    externalId?: string,
    cloudId?: string,
    contentId?: string,
    contentType?: CommentContentType,
    activationId?: string,
  ) {
    try {
      const boardId = this.getBoardId();
      const thread = this.threads.get(threadId);

      if (!externalId) {
        return [];
      }

      // should only ever be called once for any comment thread
      if (thread && thread.status !== 'loading') {
        this.emit({
          type: 'threadLoaded',
          threadId,
        });
        return thread.comments;
      }

      this.threads.set(threadId, { status: 'loading' });
      const commentList = await this.service
        .listComments({
          boardId,
          parentCommentId: externalId,
          noCache: true,
        })
        .then((response) => response.comments)
        .catch((e) => {
          if (isNotFoundError(e)) {
            this.threads.set(threadId, { status: 'fetched', comments: [] });
            this.emit({ type: 'threadNotFound', threadId });
          }

          throw e;
        });
      const comments = commentList.map((comment) =>
        commentFromConfluenceComment(comment, threadId, 'root'),
      );

      if (comments.length === 0) {
        this.threads.set(threadId, { status: 'fetched', comments: [] });
        this.emit({ type: 'threadNotFound', threadId });
        return [];
      }

      const root = comments[0];
      const replies = root.replies ?? [];
      const threadComments = [root, ...replies];

      this.threads.set(threadId, {
        status: 'fetched',
        comments: threadComments,
      });

      // pre-fetch reactions for the thread so its cached when the thread is loaded
      if (cloudId && contentId && contentType) {
        fetchReactions(cloudId, contentId, contentType, threadComments, activationId);
      }
      this.emit({ type: 'threadLoaded', threadId, commentCount: threadComments.length });

      ReliabilityMetrics.getInstance().success(MetricName.COMMENT_INTERACTION, {
        commentAction: 'view-comment',
      });
      return comments;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (isAbortableExperienceErrorMessage(msg) || isNotFoundError(e)) {
        commandService.send(AbortCommentExperienceCommand);
      } else {
        ReliabilityMetrics.getInstance().failure(MetricName.COMMENT_INTERACTION, {
          errorMessage: msg,
          commentAction: 'view-comment',
        });
      }
      throw e;
    }
  }

  public async reloadThread(threadId: string, externalId: string) {
    const boardId = this.getBoardId();

    try {
      const commentList = await this.service
        .listComments({
          boardId,
          parentCommentId: externalId,
          noCache: true,
        })
        .then((response) => response.comments);
      const comments = commentList.map((comment) =>
        commentFromConfluenceComment(comment, threadId, 'root'),
      );

      if (comments.length === 0) {
        this.threads.set(threadId, { status: 'fetched', comments: [] });
        this.emit({ type: 'threadNotFound', threadId });
        return [];
      }

      const root = comments[0];
      const replies = root.replies ?? [];

      this.threads.set(threadId, {
        status: 'fetched',
        comments: [root, ...replies],
      });
      this.emit({ type: 'threadLoaded', threadId });

      return comments;
    } catch (error) {
      if (isNotFoundError(error)) {
        this.threads.set(threadId, { status: 'fetched', comments: [] });
        this.emit({ type: 'threadNotFound', threadId });

        return [];
      } else {
        throw error;
      }
    }
  }

  public getThread(threadId: string) {
    return this.threads.get(threadId);
  }

  private async getOrInitializeThread(threadId: string, externalId: string) {
    let thread = this.threads.get(threadId);
    if (!thread) {
      await this.initializeThread(threadId, externalId);
      thread = this.threads.get(threadId) as CommentThread;
    }
    return thread;
  }

  public hasThread(threadId: string) {
    return this.threads.has(threadId);
  }

  /**
   * Used to check if a thread can be interacted with.
   * @description A thread can be interaactive if it has either loaded its data
   * already, or is able to load data (has a mapping with the remote system).
   * @param threadId
   * @returns
   */
  public hasLoadableThread(threadId: string) {
    return this.mapOfThreadIdToExternalId.has(threadId);
  }

  public getThreads(): Map<string, { visible: boolean; thread: CommentThread }> {
    const threads = new Map();
    this.threads.forEach((value, key) => {
      threads.set(key, {
        visible:
          value.status !== 'loading' &&
          value.comments.length > 0 &&
          !value.comments[0].resolved,
        thread: value,
      });
    });

    return threads;
  }

  private writeOptimisticUpdate<T>(
    threadId: string,
    externalId: string | undefined,
    meta: T,
    writer: OptimisticWriter<T>,
  ) {
    const thread = this.threads.get(threadId) ?? { status: 'fetched', comments: [] };
    if (thread.status === 'loading') {
      return;
    }

    const comments = thread.comments ?? [];
    const commentsLength = thread.comments.length;
    const comment = comments.find((c) => c.id === externalId);
    const commentsUpdated = writer(comments, comment, meta);

    this.threads.set(threadId, {
      status: 'localChange',
      comments: commentsUpdated,
    });

    this.emit({ type: 'threadChangedLocally', threadId });

    if (comment?.type === 'root') {
      this.emit({
        type: 'rootCommentChanged',
      });
    }

    // If there has been a reply added to one of the threads, the length of the
    // thread will be bigger than it was previously
    if (commentsUpdated.length > commentsLength) {
      this.emit({
        type: 'addedReplyToThread',
      });
    }

    // If there has been a reply added to one of the threads, the length of the
    // thread will be bigger than it was previously
    if (commentsUpdated.length < commentsLength) {
      this.emit({
        type: 'deletedReplyFromThread',
      });
    }
  }

  private writePersistentUpdate(
    threadId: string,
    optimisticId: string,
    result: ConfluenceComment,
    writer: PersistentWriter,
  ) {
    const thread = this.threads.get(threadId);
    if (!thread || thread.status === 'loading') {
      return;
    }

    const comment = thread.comments.find((c) => c.id === optimisticId);
    const commentsUpdated = writer(thread.comments, comment, result);

    this.threads.set(threadId, {
      status: 'fetched',
      comments: commentsUpdated,
    });

    this.emit({
      type: 'threadChangedRemotely',
      threadId,
      version: Date.now(),
    });
  }

  public getDraftId(threadId: string) {
    return `draft-${threadId}` as DraftId;
  }

  public getEditDraftId(threadId: string) {
    return `draft-edit-${threadId}` as DraftId;
  }

  public getCommentDraft(draftId: DraftId) {
    return this.commentDrafts.get(draftId);
  }

  public setCommentDraft(draftId: DraftId, draft: ADFDocument) {
    this.commentDrafts.set(draftId, draft);
  }

  public deleteCommentDraft(draftId: DraftId) {
    this.commentDrafts.delete(draftId);
  }

  private revertThread(threadId: string, thread: CommentThread) {
    this.threads.set(threadId, thread);
  }
}

let commentStore: CommentThreadStore;

export const getCommentStore = (
  service = getConfluenceService(),
  frameContext: FrameContext = currentHostFrameContext,
) => {
  if (!commentStore) {
    commentStore = new CommentThreadStore(
      () => frameContext.data.mediaCollectionId,
      service,
    );
  }

  return commentStore;
};
